import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import type { SupportedSponsorStyleId } from '@insurancemenu/imenu-components-lib/dist/hooks/useTheme';
import { GroupFilter } from './groupService';
import { DocumentsRestrictions } from './documentsService';

export interface Domain {
  id: string;
  name: string;
  logo: string;
  headerStyleId: SupportedSponsorStyleId;
  properties: Properties;
  dateDependent: DateDependent[];
}

export interface Properties {
  effectiveDatesAmount: number;
  retroActiveDaysAmount: number;
  retroQuotingDaysAmount: number;
  productsAvailableYear: number;
  productsAvailableMonth?: number;
  eligibleStates: string[];
  ineligibleSicCodes: string[];
  availableRenewalActions?: RenewalAction[];
  existAdditionalOutputs?: boolean;
  eeClassesRequired?: boolean;
  newProposalAdditionalInputs?: NewProposalAdditionalInputs;
  availablePipelineStatuses?: GroupFilter[];
  documentsRestriction?: DocumentsRestrictions;
  availableFeatures?: Feature[];
  availableAddEmployeesActions?: AddEmployeesAction[];
  availableClasses?: string[];
  allowTinOnlyGroupCreation?: boolean;
  adminOnlyAgentAssignment?: boolean;
  groupInitiationRedirect?: boolean;
}

export interface DateDependent {
  config: Config;
  effectiveDate: EffectiveDate;
  productLines: DomainProductLine[];
}

export interface Config {
  [key: string]: ConfigProps;
}

export enum RateSegmentSpecificFeature {
  SKIP_EARLY_PROPOSAL_GENERATION = 'SKIP_EARLY_PROPOSAL_GENERATION',
}

export interface ConfigProps {
  annualPremiumFormId?: string;
  censusBasedCost?: boolean;
  resubmitIneligible: boolean;
  proposalConfigIneligible?: boolean;
  rfqFormId?: string;
  proposalDocumentTypes?: string[];
  featureFlags?: RateSegmentSpecificFeature[];
}

export interface EffectiveDate {
  from: string;
  to: string;
}

export interface DomainProductLine {
  name: string;
  rateSegment: RateSegment;
  bcFormId: string;
  dependentOnProductLineId?: ProductLineId;
  productLineId: ProductLineId;
  configurablePlans: boolean;
  browsingConfigurablePlans?: boolean;
  dontProvideIneligible: boolean;
  policyAttributesFormId?: string;
  planAttributesBaseFormId?: string;
  planAttributesCustomFormId?: string;
  browsingType: BrowsingType;
  overrideRatesEligible: boolean;
  rfqForms?: RFQCarrierForms;
  dynamicPortfolios: DynamicPortfolioConfig;
  available: boolean;
}

export type CarrierDynamicPortfolioConfig = {
  [carrierId: string]: number;
} & {
  def?: number;
};

export interface DynamicPortfolioConfig {
  creationAvailable: boolean;
  creationRoles: SupportedUserRoles[];
  carrierPortfolioSize: CarrierDynamicPortfolioConfig;
}

export type RFQCarrierForms = {
  [carrierId: string]: RFQForms;
} & {
  def?: RFQForms;
};

export interface RFQForms {
  rateRfqFormId?: string;
}

export type BrowsingType = 'RFQ' | 'SHELF';

export interface NewProposalAdditionalInputs {
  required: string[];
  optional: string[];
}

export interface InputFilter {
  effectiveDate: string;
  zipCode: string;
  state: string;
  county: string;
  agencyId: string;
  sponsorId: string;
  sic?: string;
  includeUnavailable?: boolean;
}

export enum RenewalAction {
  RENEW_AS_IS = 'RENEW_AS_IS',
  CANCEL_COVERAGE = 'CANCEL_COVERAGE',
  CHANGE_PLAN = 'CHANGE_PLAN',
  BROWSE_INSTALLED_PERIOD = 'BROWSE_INSTALLED_PERIOD',
}

export enum Feature {
  POST_SUBMIT_CHECKLIST = 'POST_SUBMIT_CHECKLIST',
  E_SIGNATURE = 'E_SIGNATURE',
  CROSS_SELL_MODAL = 'CROSS_SELL_MODAL',
  INITIAL_SEED = 'INITIAL_SEED',
  DISABLE_CUSTOM_ATTRIBUTES = 'DISABLE_CUSTOM_ATTRIBUTES',
  SMALL_GROUP_CHECKLIST_QUOTING = 'SMALL_GROUP_CHECKLIST_QUOTING',
  E_MAINTAIN = 'E_MAINTAIN',
  SHOW_QUOTING_UW_GUIDELINES = 'SHOW_QUOTING_UW_GUIDELINES',
  SELF_ENROLL_ENABLED = 'SELF_ENROLL_ENABLED',
  CENSUS_BYPASS = 'CENSUS_BYPASS',
}

export enum AddEmployeesAction {
  CENSUS_UPLOAD = 'CENSUS_UPLOAD',
  FULL_FORM = 'FULL_FORM',
  BASIC_FORM = 'BASIC_FORM',
  SELF_REGISTER_LINK = 'SELF_REGISTER_LINK',
}
